import { DialogForm } from '@/features/dialog/dialog-form';
import { RichTextInput } from '@/features/rich-text/components/rich-text-input';
import type { AIActionType } from '@/features/rich-text/hooks/use-improve-writing';
import { useLocale } from '@/lib/hooks/use-locale';
import { getTextContent } from '@/lib/rich-text/get-text-content';
import type { CrmNoteInput } from '@/server/dto/crm-note-input-dto';
import type { CrmNoteType } from '@plato/data/prisma';
import type { Control, FieldErrors } from 'react-hook-form';
import { Controller } from 'react-hook-form';

export type NoteFormFieldsProps<T extends CrmNoteInput> = {
    noteType: CrmNoteType;
    control: Control<T>;
    errors: FieldErrors<T>;
    onAIInteraction?: (action: AIActionType, sessionId: string) => void;
    noteId?: string;
};

export const NoteFormFields = <T extends CrmNoteInput>({
    noteType,
    control,
    errors,
    onAIInteraction,
    noteId,
}: NoteFormFieldsProps<T>) => {
    const { t } = useLocale();

    const getPlaceholderByType = (noteType: CrmNoteType) => {
        switch (noteType) {
            case 'VISIT':
            case 'EVENT':
                return t('notes__placeholder__event', { ns: 'notes' });
            case 'PHONE_CALL':
                return t('notes__placeholder__phone_call', { ns: 'notes' });
            case 'NOTE':
            default:
                return t('notes__placeholder__note', { ns: 'notes' });
        }
    };

    return (
        <DialogForm.ItemList>
            {/* Title/Description Field */}
            <DialogForm.Item>
                <DialogForm.Body className="box-border py-0">
                    <Controller
                        name="title"
                        control={control as Control<CrmNoteInput>}
                        rules={{
                            validate: (value) => {
                                if (noteType !== 'NOTE') {
                                    return true;
                                }

                                const textContent = getTextContent(value);
                                return !!textContent || t('form__text_required', { ns: 'common' });
                            },
                        }}
                        render={({ field }) => (
                            <RichTextInput
                                entityType="note"
                                entityId={noteId}
                                placeholder={getPlaceholderByType(noteType)}
                                onAIInteraction={onAIInteraction}
                                error={!!errors.title}
                                {...field}
                            />
                        )}
                    />
                </DialogForm.Body>
                <DialogForm.Error error={errors.title} />
            </DialogForm.Item>
        </DialogForm.ItemList>
    );
};
